import { Fragment, useState } from 'react'
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react'
import { formatMoney } from './DailyReportTable'
import AttributionModelDetails from './AttributionModelDetails'
import { DRILLABLE_CHANNELS, mergeCampaignDrill, drillValue } from '../lib/attributionDrill'

export const ATTRIBUTION_CHANNEL_LABELS = {
  meta: 'Meta', google: 'Google', reddit: 'Reddit',
  bing: 'Bing', tiktok: 'TikTok', email: 'Email', sms: 'SMS', direct: 'Direct',
  organic_search: 'Organic Search', paid_branded_search: 'Paid Branded Search',
  outside_window: 'Outside attribution window', sales_team: 'Sales Team', unattributed: 'Unattributed',
}

const LEVEL_NAMES = ['campaign', 'adset', 'ad']
const INDENT = ['pl-8', 'pl-12', 'pl-16']

/**
 * Channel credit under each model. With `onLoadCampaigns`, a paid channel's row
 * expands into its ad_spend campaigns, each campaign into adsets, each adset
 * into ads — loaded once per channel and kept until the comparison reloads.
 */
export default function AttributionCreditTable({ comparison, metric = 'revenue', scope = 'all', lookbackDays = 'all', onLoadCampaigns = null }) {
  const [drills, setDrills] = useState({}) // channel -> { loading, error, tree }
  const [open, setOpen] = useState({}) // row key -> bool
  const channels = [...new Set(comparison.models.flatMap(model => model.rows.map(row => row.channel)))]
    .sort((a, b) => a === 'unattributed' ? 1 : b === 'unattributed' ? -1 : a.localeCompare(b))
  const columns = comparison.models.flatMap(model => (metric === 'both' ? ['revenue', 'orders'] : [metric]).map(key => ({ model, metric: key })))
  const show = (value, key) => key === 'revenue' ? formatMoney(value) : value.toLocaleString('en-US', { maximumFractionDigits: 3 })
  const toggle = key => setOpen(prev => ({ ...prev, [key]: !prev[key] }))

  const toggleChannel = channel => {
    toggle(channel)
    if (drills[channel] && !drills[channel].error) return
    setDrills(prev => ({ ...prev, [channel]: { loading: true } }))
    onLoadCampaigns(channel)
      .then(data => setDrills(prev => ({ ...prev, [channel]: { tree: mergeCampaignDrill(data, metric) } })))
      .catch(err => setDrills(prev => ({ ...prev, [channel]: { error: err.response?.data?.error?.message || 'Failed to load campaigns' } })))
  }

  const nodeRows = (rows, depth, parentKey) => rows.flatMap(row => {
    const key = `${parentKey}|${row.key}`
    const expandable = row.children.length > 0
    return [
      <tr key={key} className="border-t bg-muted/10 hover:bg-muted/30">
        <th scope="row" className={`p-2 ${INDENT[depth]} text-left font-normal`}>
          <button type="button" disabled={!expandable} onClick={() => toggle(key)} className="inline-flex items-start gap-1 text-left disabled:cursor-default">
            {expandable
              ? (open[key] ? <ChevronDown className="mt-0.5 h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0" />)
              : <span className="w-3.5 shrink-0" />}
            <span>
              {row.label}
              <span className="ml-1.5 text-xs text-muted-foreground">{LEVEL_NAMES[depth]}</span>
            </span>
          </button>
        </th>
        {columns.map(({ model, metric: m }) => <td key={`${model.id}-${m}`} className="p-2 text-right">{show(drillValue(row, model.id, m), m)}</td>)}
      </tr>,
      ...(expandable && open[key] ? nodeRows(row.children, depth + 1, key) : []),
    ]
  })

  const drillRows = channel => {
    const drill = drills[channel]
    const span = columns.length + 1
    if (!drill || drill.loading) {
      return <tr className="border-t"><td colSpan={span} className="p-2 pl-8 text-muted-foreground"><Loader2 className="mr-1.5 inline h-3.5 w-3.5 animate-spin" />Loading campaigns…</td></tr>
    }
    if (drill.error) return <tr className="border-t"><td colSpan={span} className="p-2 pl-8 text-destructive">{drill.error}</td></tr>
    const { campaigns, unmatched } = drill.tree
    return <>
      {nodeRows(campaigns, 0, channel)}
      <tr className="border-t bg-muted/10">
        <th scope="row" className="p-2 pl-8 text-left font-normal text-muted-foreground" title="Credit whose click names no campaign in ad spend, such as a missing or unrecognised UTM">Not matched to a campaign</th>
        {columns.map(({ model, metric: m }) => <td key={`${model.id}-${m}`} className="p-2 text-right text-muted-foreground">{show(unmatched[model.id]?.[m] || 0, m)}</td>)}
      </tr>
    </>
  }

  return (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm tabular-nums">
                  <caption className="sr-only">Attribution model comparison by channel</caption>
                  <thead className="bg-muted/50">
                    <tr>
                      <th scope="col" className="p-3 text-left">Channel</th>
                      {columns.map(({ model, metric: key }) => <th scope="col" key={`${model.id}-${key}`} className="p-3 text-right min-w-36"><AttributionModelDetails model={model} lookbackDays={lookbackDays} scope={scope} attributionWindow={comparison.attributionWindow} accountingMode={comparison.accountingMode} compact />{metric === 'both' && <span className="block text-xs font-normal text-muted-foreground">{key === 'revenue' ? 'Attributed revenue' : 'Order credit'}</span>}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {channels.map(channel => {
                      const drillable = Boolean(onLoadCampaigns) && DRILLABLE_CHANNELS.includes(channel)
                      const label = ATTRIBUTION_CHANNEL_LABELS[channel] || channel
                      return (
                        <Fragment key={channel}>
                          <tr className="border-t hover:bg-muted/30">
                            <th scope="row" className="p-3 text-left font-medium whitespace-nowrap">
                              {drillable ? (
                                <button type="button" onClick={() => toggleChannel(channel)} className="inline-flex items-center gap-1" title="Drill into campaigns, adsets and ads">
                                  {open[channel] ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                                  {label}
                                </button>
                              ) : label}
                            </th>
                            {columns.map(({ model, metric: key }) => (
                              <td key={`${model.id}-${key}`} className="p-3 text-right">{show(model.rows.find(row => row.channel === channel)?.[key] || 0, key)}</td>
                            ))}
                          </tr>
                          {drillable && open[channel] && drillRows(channel)}
                        </Fragment>
                      )
                    })}
                  </tbody>
                  <tfoot className="border-t bg-muted/50 font-semibold">
                    <tr><th scope="row" className="p-3 text-left">Within attribution window</th>{columns.map(({ model, metric: key }) => <td key={`${model.id}-${key}`} className="p-3 text-right">{show(model.rows.filter(row => row.channel !== 'outside_window').reduce((sum, row) => sum + row[key], 0), key)}{model.reconciles === false && <span className="block text-xs font-normal text-muted-foreground">Overlapping credit</span>}</td>)}</tr>
                  </tfoot>
                </table>
              </div>
  )
}
